// Modal de criar/editar informativo — mockup "Modal Informativo": duas
// colunas, formulário à esquerda e a pré-visualização "Como aparece no sino"
// à direita, renderizada do estado atual do formulário (por isso qualquer
// mudança aqui deve andar junto com components/AnnouncementsPopover).
import React, { useState, useEffect, useRef, useMemo } from "react";

import { toast } from "react-toastify";
import { head } from "lodash";
import { makeStyles, alpha, useTheme } from "@material-ui/core/styles";
import TextField from "@material-ui/core/TextField";
import Switch from "@material-ui/core/Switch";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";

import CampaignIcon from "@material-ui/icons/RecordVoiceOver";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import NotificationsIcon from "@material-ui/icons/Notifications";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";

import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import AerosDialog from "../AerosDialog";
import AerosConfirmModal from "../AerosConfirmModal";
import useAerosModalStyles from "../../styles/aerosModalStyles";
import { AEROS, stateText } from "../../styles/aerosTokens";
import { useDate } from "../../hooks/useDate";
import {
  priorityMeta,
  charCount,
  excerpt,
} from "../../pages/Annoucements/announcementRules";

const PRIORITY_OPTIONS = [1, 2, 3];

const useStyles = makeStyles((theme) => {
  const isLight = theme.palette.type === "light";
  const border = isLight ? theme.palette.divider : "rgba(255,255,255,0.07)";
  const borderInput = isLight
    ? theme.palette.aeros.borderStrong
    : "rgba(255,255,255,0.1)";
  return {
    split: {
      display: "grid",
      gridTemplateColumns: "minmax(0,1.25fr) minmax(0,1fr)",
      minHeight: 0,
      [theme.breakpoints.down("sm")]: { gridTemplateColumns: "1fr" },
    },
    formCol: {
      padding: "20px 24px",
      display: "flex",
      flexDirection: "column",
      gap: 16,
      borderRight: `1px solid ${border}`,
      [theme.breakpoints.down("sm")]: { borderRight: "none" },
    },
    previewCol: {
      padding: "20px 24px",
      backgroundColor: isLight ? theme.palette.aeros.surface2 : "#0E1119",
      display: "flex",
      flexDirection: "column",
    },
    microLabel: {
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.1em",
      textTransform: "uppercase",
      color: theme.palette.aeros.textTertiary,
      marginBottom: 12,
    },
    labelRow: { display: "flex", alignItems: "center", marginBottom: 6 },
    counter: {
      marginLeft: "auto",
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      fontVariantNumeric: "tabular-nums",
    },
    required: { color: AEROS.error, marginLeft: 3 },

    // ---- segmented control de prioridade ----
    segmented: {
      display: "flex",
      gap: 4,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 10,
      padding: 3,
    },
    segment: {
      flex: 1,
      textAlign: "center",
      fontSize: 12,
      fontWeight: 600,
      padding: "6px 0",
      borderRadius: 7,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 5,
      cursor: "pointer",
      border: "none",
      background: "none",
      fontFamily: "inherit",
      color: theme.palette.text.secondary,
    },
    segmentDot: { width: 6, height: 6, borderRadius: "50%", flex: "none" },

    // ---- status ----
    statusBox: {
      border: `1px solid ${borderInput}`,
      backgroundColor: theme.palette.aeros.surface,
      borderRadius: 10,
      padding: "1px 6px 1px 12px",
      display: "flex",
      alignItems: "center",
      gap: 10,
      height: 38,
      boxSizing: "border-box",
    },
    statusLabel: { fontSize: 13, fontWeight: 600, flex: 1 },

    // ---- imagem ----
    imageRow: {
      display: "flex",
      alignItems: "center",
      gap: 10,
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${border}`,
      borderRadius: 10,
      padding: "9px 12px",
    },
    thumb: {
      width: 44,
      height: 32,
      borderRadius: 6,
      flex: "none",
      objectFit: "cover",
      background: "linear-gradient(135deg,rgba(160,68,255,0.5),rgba(34,211,238,0.5))",
    },
    imageName: {
      fontSize: 12.5,
      fontWeight: 600,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    },
    imageMeta: { fontSize: 11, color: theme.palette.text.secondary },
    imageButton: {
      fontSize: 11.5,
      fontWeight: 600,
      color: theme.palette.text.secondary,
      border: `1px solid ${isLight ? theme.palette.aeros.borderStrong : "rgba(255,255,255,0.12)"}`,
      borderRadius: 8,
      padding: "4px 10px",
      background: "none",
      cursor: "pointer",
      fontFamily: "inherit",
      "&:hover": { color: theme.palette.text.primary },
    },
    imageDelete: {
      padding: 4,
      color: theme.palette.aeros.textTertiary,
      "&:hover": { color: stateText(theme, AEROS.error, "error") },
      "& .MuiSvgIcon-root": { fontSize: 15 },
    },

    // ---- prévia do sino ----
    bell: {
      backgroundColor: theme.palette.aeros.surface,
      border: `1px solid ${borderInput}`,
      borderRadius: 14,
      boxShadow: "0 12px 30px rgba(0,0,0,0.5)",
      overflow: "hidden",
    },
    bellHead: {
      display: "flex",
      alignItems: "center",
      gap: 8,
      padding: "12px 14px",
      borderBottom: `1px solid ${border}`,
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: stateText(theme, "#C08BFF", "purple"),
      },
    },
    bellTitle: { fontSize: 13, fontWeight: 700, flex: 1 },
    bellCount: {
      fontSize: 10.5,
      fontWeight: 700,
      color: "#07080D",
      backgroundColor: AEROS.cyan,
      borderRadius: 99,
      padding: "1px 7px",
    },
    bellBody: {
      padding: "12px 14px",
      display: "flex",
      flexDirection: "column",
      gap: 10,
    },
    bellCard: { borderRadius: 10, padding: "10px 12px" },
    bellCardHead: {
      display: "flex",
      alignItems: "center",
      gap: 6,
      marginBottom: 6,
    },
    bellPriority: {
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: "0.08em",
      textTransform: "uppercase",
    },
    bellWhen: {
      fontSize: 10.5,
      color: theme.palette.aeros.textTertiary,
      marginLeft: "auto",
    },
    bellCardTitle: { fontSize: 13, fontWeight: 600, lineHeight: 1.35 },
    bellImage: {
      height: 70,
      width: "100%",
      objectFit: "cover",
      borderRadius: 8,
      margin: "8px 0",
      display: "block",
      background: "linear-gradient(135deg,rgba(160,68,255,0.45),rgba(34,211,238,0.45))",
    },
    bellText: {
      fontSize: 12,
      color: isLight ? theme.palette.text.secondary : "#C9CDD8",
      lineHeight: 1.5,
    },
    bellEmpty: {
      fontSize: 12,
      color: theme.palette.aeros.textTertiary,
      lineHeight: 1.5,
    },
    previewHint: {
      marginTop: "auto",
      paddingTop: 16,
      fontSize: 11,
      color: theme.palette.aeros.textTertiary,
      display: "flex",
      alignItems: "flex-start",
      gap: 5,
      lineHeight: 1.5,
      "& .MuiSvgIcon-root": { fontSize: 13, marginTop: 2, flexShrink: 0 },
    },
    footerNote: { fontSize: 11.5, color: theme.palette.aeros.textTertiary },
  };
});

const AnnouncementModal = ({
  open,
  onClose,
  announcementId,
  reload,
  resetPagination,
}) => {
  const classes = useStyles();
  const m = useAerosModalStyles();
  const theme = useTheme();
  const { datetimeToClient } = useDate();

  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  const [priority, setPriority] = useState(3);
  const [status, setStatus] = useState(true);
  const [mediaPath, setMediaPath] = useState(null);
  const [mediaName, setMediaName] = useState(null);
  const [updatedAt, setUpdatedAt] = useState(null);
  const [attachment, setAttachment] = useState(null);
  const [touched, setTouched] = useState(false);
  const [saving, setSaving] = useState(false);
  const [confirmRemoveMedia, setConfirmRemoveMedia] = useState(false);
  const attachmentFile = useRef(null);

  const resetState = () => {
    setTitle("");
    setText("");
    setPriority(3);
    setStatus(true);
    setMediaPath(null);
    setMediaName(null);
    setUpdatedAt(null);
    setAttachment(null);
    setTouched(false);
  };

  useEffect(() => {
    if (!open) return;
    setTouched(false);
    setAttachment(null);

    if (!announcementId) {
      resetState();
      return;
    }

    (async () => {
      try {
        const { data } = await api.get(`/announcements/${announcementId}`);
        setTitle(data?.title || "");
        setText(data?.text || "");
        setPriority(Number(data?.priority) || 3);
        setStatus(Boolean(data?.status));
        setMediaPath(data?.mediaPath || null);
        setMediaName(data?.mediaName || null);
        setUpdatedAt(data?.updatedAt || null);
      } catch (err) {
        toastError(err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [announcementId, open]);

  // URL local do arquivo recém-escolhido, para a miniatura e a prévia.
  const attachmentUrl = useMemo(
    () => (attachment ? URL.createObjectURL(attachment) : null),
    [attachment]
  );
  useEffect(
    () => () => {
      if (attachmentUrl) URL.revokeObjectURL(attachmentUrl);
    },
    [attachmentUrl]
  );

  const previewImage = attachmentUrl || mediaPath || null;
  const shownName = attachment?.name || mediaName || null;
  const shownSize = attachment
    ? `${Math.round(attachment.size / 1024)} KB`
    : null;

  const errors = {
    title: !title.trim() ? i18n.t("announcements.dialog.titleRequired") : "",
    text: !text.trim() ? i18n.t("announcements.dialog.textRequired") : "",
  };
  const hasError = Boolean(errors.title || errors.text);
  const shown = (key) => (touched ? errors[key] : "");

  const handleClose = () => {
    resetState();
    onClose();
  };

  const handleAttachmentFile = (e) => {
    const file = head(e.target.files);
    if (file) setAttachment(file);
  };

  const afterWrite = () => {
    if (typeof reload === "function") reload();
    if (typeof resetPagination === "function") resetPagination();
  };

  const handleSave = async () => {
    setTouched(true);
    if (hasError) return;
    setSaving(true);

    const announcementData = {
      title: title.trim(),
      text: text.trim(),
      priority,
      status,
    };

    const upload = async (id) => {
      const formData = new FormData();
      formData.append("typeArch", "announcements");
      formData.append("file", attachment);
      await api.post(`/announcements/${id}/media-upload`, formData);
    };

    try {
      if (announcementId) {
        await api.put(`/announcements/${announcementId}`, announcementData);
        if (attachment != null) await upload(announcementId);
      } else {
        const { data } = await api.post("/announcements", announcementData);
        if (attachment != null) await upload(data.id);
      }
      toast.success(i18n.t("announcements.toasts.success"));
      afterWrite();
      handleClose();
    } catch (err) {
      toastError(err);
    } finally {
      setSaving(false);
    }
  };

  const deleteMedia = async () => {
    setConfirmRemoveMedia(false);
    if (attachment) {
      setAttachment(null);
      if (attachmentFile.current) attachmentFile.current.value = null;
      return;
    }

    if (mediaPath && announcementId) {
      try {
        await api.delete(`/announcements/${announcementId}/media-upload`);
        setMediaPath(null);
        setMediaName(null);
        toast.success(i18n.t("announcements.toasts.deleted"));
        afterWrite();
      } catch (err) {
        toastError(err);
      }
    }
  };

  const pr = priorityMeta(priority);

  return (
    <>
      <AerosConfirmModal
        open={confirmRemoveMedia}
        onClose={() => setConfirmRemoveMedia(false)}
        onConfirm={deleteMedia}
        icon={<DeleteOutlineIcon />}
        tone="danger"
        title={i18n.t("announcements.dialog.imageRemove")}
        description={i18n.t("announcements.confirmationModal.deleteMessage")}
        confirmLabel={i18n.t("announcements.dialog.imageRemove")}
      />

      <AerosDialog
        open={open}
        onClose={handleClose}
        width={860}
        flushBody
        title={i18n.t(
          announcementId ? "announcements.dialog.update" : "announcements.dialog.add"
        )}
        subtitle={i18n.t("announcements.dialog.subtitle")}
        onSubmit={handleSave}
        loading={saving}
        submitLabel={i18n.t("announcements.dialog.save")}
        submitIcon={<CampaignIcon />}
        cancelLabel={i18n.t("announcements.dialog.buttons.cancel")}
        footerLeft={
          updatedAt ? (
            <span className={classes.footerNote}>
              {i18n.t("announcements.dialog.lastEdit", {
                when: datetimeToClient(updatedAt),
              })}
            </span>
          ) : null
        }
      >
        <div className={classes.split}>
          {/* ---- formulário ---- */}
          <div className={classes.formCol}>
            <div>
              <label
                className={`${m.fieldLabel} ${
                  !shown("title") && title ? m.fieldLabelFocus : ""
                }`}
                htmlFor="announcement-title"
              >
                {i18n.t("announcements.dialog.form.title")}
                <span className={classes.required}>*</span>
              </label>
              <TextField
                id="announcement-title"
                variant="outlined"
                className={m.input}
                value={title}
                autoFocus
                onChange={(e) => setTitle(e.target.value)}
                onBlur={() => setTouched(true)}
                error={Boolean(shown("title"))}
                helperText={shown("title")}
              />
            </div>

            <div>
              <div className={classes.labelRow}>
                <label
                  className={`${m.fieldLabel} ${
                    !shown("text") && text ? m.fieldLabelFocus : ""
                  }`}
                  htmlFor="announcement-text"
                  style={{ marginBottom: 0 }}
                >
                  {i18n.t("announcements.dialog.form.text")}
                  <span className={classes.required}>*</span>
                </label>
                <span className={classes.counter}>
                  {i18n.t("announcements.dialog.charCount", {
                    count: charCount(text),
                  })}
                </span>
              </div>
              <TextField
                id="announcement-text"
                variant="outlined"
                className={m.input}
                value={text}
                multiline
                minRows={5}
                onChange={(e) => setText(e.target.value)}
                onBlur={() => setTouched(true)}
                error={Boolean(shown("text"))}
                helperText={shown("text")}
              />
            </div>

            <div className={m.grid2} style={{ gridTemplateColumns: "1.4fr 1fr" }}>
              <div>
                <span className={m.fieldLabel}>
                  {i18n.t("announcements.dialog.form.priority")}
                </span>
                <div className={classes.segmented} role="group">
                  {PRIORITY_OPTIONS.map((value) => {
                    const meta = priorityMeta(value);
                    const on = priority === value;
                    return (
                      <button
                        key={value}
                        type="button"
                        className={classes.segment}
                        aria-pressed={on}
                        onClick={() => setPriority(value)}
                        style={
                          on
                            ? {
                                backgroundColor: alpha(meta.color, 0.18),
                                color: theme.palette.text.primary,
                              }
                            : undefined
                        }
                      >
                        <span
                          className={classes.segmentDot}
                          style={{ backgroundColor: meta.color }}
                        />
                        {meta.label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div>
                <span className={m.fieldLabel}>
                  {i18n.t("announcements.dialog.form.status")}
                </span>
                <div className={classes.statusBox}>
                  <span className={classes.statusLabel}>
                    {status
                      ? i18n.t("announcements.active")
                      : i18n.t("announcements.inactive")}
                  </span>
                  <Switch
                    color="primary"
                    checked={status}
                    onChange={(e) => setStatus(e.target.checked)}
                    inputProps={{
                      "aria-label": i18n.t("announcements.dialog.form.status"),
                    }}
                  />
                </div>
              </div>
            </div>

            <div>
              <span className={m.fieldLabel}>
                {i18n.t("announcements.dialog.image")}{" "}
                <span style={{ fontWeight: 500, opacity: 0.8 }}>
                  {i18n.t("announcements.dialog.imageHint")}
                </span>
              </span>
              <input
                type="file"
                accept="image/png,image/jpeg"
                ref={attachmentFile}
                style={{ display: "none" }}
                id="announcement-media"
                onChange={handleAttachmentFile}
              />
              {previewImage ? (
                <div className={classes.imageRow}>
                  <img
                    src={previewImage}
                    alt=""
                    className={classes.thumb}
                    onError={(e) => {
                      e.currentTarget.style.visibility = "hidden";
                    }}
                  />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.imageName}>{shownName || "—"}</div>
                    {shownSize && (
                      <div className={classes.imageMeta}>{shownSize}</div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={classes.imageButton}
                    onClick={() => attachmentFile.current?.click()}
                  >
                    {i18n.t("announcements.dialog.imageChange")}
                  </button>
                  <Tooltip title={i18n.t("announcements.dialog.imageRemove")}>
                    <IconButton
                      className={classes.imageDelete}
                      aria-label={i18n.t("announcements.dialog.imageRemove")}
                      onClick={() =>
                        attachment ? deleteMedia() : setConfirmRemoveMedia(true)
                      }
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </Tooltip>
                </div>
              ) : (
                <button
                  type="button"
                  className={classes.imageButton}
                  style={{ width: "100%", padding: "10px 12px" }}
                  onClick={() => attachmentFile.current?.click()}
                >
                  {i18n.t("announcements.dialog.imagePick")}
                </button>
              )}
            </div>
          </div>

          {/* ---- prévia do sino ---- */}
          <div className={classes.previewCol}>
            <div className={classes.microLabel}>
              {i18n.t("announcements.dialog.previewTitle")}
            </div>
            <div className={classes.bell}>
              <div className={classes.bellHead}>
                <NotificationsIcon />
                <span className={classes.bellTitle}>
                  {i18n.t("announcements.bell.title")}
                </span>
                <span className={classes.bellCount}>1</span>
              </div>
              <div className={classes.bellBody}>
                <div
                  className={classes.bellCard}
                  style={{
                    backgroundColor: alpha(pr.color, 0.08),
                    border: `1px solid ${alpha(pr.color, 0.25)}`,
                  }}
                >
                  <div className={classes.bellCardHead}>
                    <span
                      className={classes.bellPriority}
                      style={{ color: stateText(theme, pr.color, pr.stateKey) }}
                    >
                      {pr.label}
                    </span>
                    <span className={classes.bellWhen}>
                      {i18n.t("announcements.when.now")}
                    </span>
                  </div>
                  {title || text ? (
                    <>
                      <div className={classes.bellCardTitle}>{title}</div>
                      {previewImage && (
                        <img
                          src={previewImage}
                          alt=""
                          className={classes.bellImage}
                          onError={(e) => {
                            e.currentTarget.style.visibility = "hidden";
                          }}
                        />
                      )}
                      <div className={classes.bellText}>{excerpt(text, 160)}</div>
                    </>
                  ) : (
                    <div className={classes.bellEmpty}>
                      {i18n.t("announcements.dialog.previewEmpty")}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className={classes.previewHint}>
              <InfoOutlinedIcon />
              {i18n.t("announcements.dialog.previewHint")}
            </div>
          </div>
        </div>
      </AerosDialog>
    </>
  );
};

export default AnnouncementModal;
